import { useState } from "react";

import clsx from "clsx";

import { Modal } from "@components/shared/modal";
import { useUser } from "@components/user-context";
import {
  ButtonGroup,
  ModalPanel,
  NoticeButton,
  PrimaryButton,
  QuickSelectInfoButton,
  QuickSelectNoticeButton,
  SecondaryButton,
  TertiaryButton,
} from "@components/v2";
import { LearnMoreTooltip } from "@features/sales-v3/top-level-filters/components/learn-more-tooltip";
import { useUserAttributionSettings } from "@hooks/use-user-attribution-settings";
import {
  type IMCAttributionWindowDays,
  type INBAccountingMode,
} from "@north-beam/nb-common";
import { useAttributionWindowOptions } from "@pages/sales/report-body-control/attribution-window-select";

const ATTRIBUTION_MODEL_DESCRIPTION: Record<string, string> = {
  linear:
    "Splits credit equally across all tracked touchpoints in the customer journey.",
  first_touch:
    "Gives 100% credit to the first touchpoint in the customer journey.",
  last_touch: "Gives 100% credit to the last touchpoint before a purchase.",
  last_touch_non_direct:
    "Gives 100% credit to the last tracked touchpoint before conversion, ignoring direct visits.",
  northbeam_custom:
    "Splits credit equally across all touchpoints excluding lower funnel touchpoints " +
    "to emphasize the demand generation portion of the customer journey.",
  northbeam_custom__va:
    "Pairs Clicks Only attribution with a machine-learning model for view-based impact.",
  northbeam_custom__enh:
    "Pairs Clicks Only attribution with deterministic ad-platform events for view-based impact.",
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const textShadowStyle = {
  textShadow: "1px 1px 0px var(--v2-bg-primary, #FFF)",
};

type AttributionModelOption = {
  value: string;
  label: string;
};

type AttributionWindowOption = {
  value: IMCAttributionWindowDays;
  label: string;
};

const shouldShowCash = (path: string): boolean => {
  return !path.includes("product-analytics");
};

const getInitialState = (
  path: string,
  globalAttributionModel: string,
  globalAttributionWindow: IMCAttributionWindowDays,
  globalAccountingMode: INBAccountingMode,
) => {
  const isProductAnalytics = path.includes("product-analytics");

  if (isProductAnalytics) {
    return {
      attributionModel: "northbeam_custom",
      attributionWindow:
        globalAttributionWindow === "1"
          ? "1"
          : ("30" as IMCAttributionWindowDays),
      accountingMode: "accrual" as INBAccountingMode,
    };
  }

  return {
    attributionModel: globalAttributionModel,
    attributionWindow: globalAttributionWindow,
    accountingMode: globalAccountingMode,
  };
};

const getFilteredOptions = (
  path: string,
  attributionModelOptions: AttributionModelOption[],
  attributionWindowOptions: AttributionWindowOption[],
) => {
  const isProductAnalytics = path.includes("product-analytics");

  if (isProductAnalytics) {
    return {
      attributionModelOptions: attributionModelOptions.filter(
        (option) => option.value === "northbeam_custom",
      ),
      attributionWindowOptions: attributionWindowOptions.filter(
        (option) => option.value === "1" || option.value === "30",
      ),
    };
  }

  return {
    attributionModelOptions,
    attributionWindowOptions,
  };
};

export const AttributionModeWindowModal = (props: Props) => {
  return (
    <Modal
      variant="v2"
      hideCloseButton
      bodyClassName="flex flex-col"
      contentClassName="max-w-[900px] h-auto flex flex-col"
      {...props}
    >
      <AttributionModeWindowContent onClose={props.onClose} />
    </Modal>
  );
};

const AttributionModeWindowContent = ({ onClose }: { onClose: () => void }) => {
  const { user } = useUser();
  const { pathname } = window.location;

  const {
    attributionModel: globalAttributionModel,
    attributionWindow: globalAttributionWindow,
    accountingMode: globalAccountingMode,
    setAttributionModel: setGlobalAttributionModel,
    setAttributionWindow: setGlobalAttributionWindow,
    setAccountingMode: setGlobalAccountingMode,
  } = useUserAttributionSettings();

  const { reactSelectOptions: allAttributionModelOptions } =
    user.attributionMethodEnum;
  const { options: allAttributionWindowOptions } = useAttributionWindowOptions({
    timeGranularity: "daily",
  });

  const { attributionModelOptions, attributionWindowOptions } =
    getFilteredOptions(
      pathname,
      allAttributionModelOptions,
      allAttributionWindowOptions,
    );

  const initialState = getInitialState(
    pathname,
    globalAttributionModel,
    globalAttributionWindow,
    globalAccountingMode,
  );

  const [localAttributionModel, setLocalAttributionModel] = useState(
    initialState.attributionModel,
  );
  const [localAttributionWindow, setLocalAttributionWindow] =
    useState<IMCAttributionWindowDays>(initialState.attributionWindow);
  const [localAccountingMode, setLocalAccountingMode] =
    useState<INBAccountingMode>(initialState.accountingMode);

  const isAccrual = localAccountingMode === "accrual";

  return (
    <ModalPanel className="w-full flex flex-col overflow-hidden">
      {shouldShowCash(pathname) && (
        <div className="flex gap-2 mb-6">
          <QuickSelectInfoButton
            data-autofocus={isAccrual ? true : undefined}
            className="flex-1 text-sm"
            style={textShadowStyle}
            onClick={() => {
              setLocalAccountingMode("accrual");
              setLocalAttributionWindow("1");
            }}
            selected={localAccountingMode === "accrual"}
          >
            Accrual Performance
          </QuickSelectInfoButton>
          <QuickSelectNoticeButton
            data-autofocus={!isAccrual ? true : undefined}
            className="flex-1 text-sm"
            style={textShadowStyle}
            onClick={() => {
              setLocalAccountingMode("cash");
              setLocalAttributionWindow("infinity");
            }}
            selected={localAccountingMode === "cash"}
          >
            Cash Snapshot
          </QuickSelectNoticeButton>
        </div>
      )}
      {isAccrual && (
        <div className="max-w-[480px] mb-6">
          <div className="text-xs font-semibold mb-2 text-v2-primary">
            Attribution Window
            <LearnMoreTooltip
              title="Attribution Window"
              content={
                "An attribution window is the period of time after an ad interaction " +
                "(such as an ad click or video view) during which a conversion, " +
                "such as a purchase, is recorded in the Northbeam system."
              }
              link="https://docs.northbeam.io/docs/attribution-windows"
            />
          </div>
          <ButtonGroup
            options={attributionWindowOptions}
            onChange={(value) => {
              setLocalAttributionWindow(value);
            }}
            value={localAttributionWindow}
          />
        </div>
      )}
      <div className="mb-6 flex flex-col overflow-hidden">
        <div className="text-xs font-semibold mb-2 text-v2-primary">
          Attribution Model
          <LearnMoreTooltip
            title="Attribution Model"
            content={
              "Attribution models allow you to choose the method by which " +
              "each ad interaction receives credit for your conversions."
            }
            link="https://docs.northbeam.io/docs/attribution-models"
          />
        </div>
        <div className="border border-v2-tertiary rounded-lg text-sm overflow-auto text-v2-primary">
          {attributionModelOptions.map((row, index) => {
            return (
              <div
                key={row.value}
                className={clsx("h-14 flex border-v2-tertiary", {
                  "bg-v2-secondary": index % 2 === 1,
                  "border-b": index < attributionModelOptions.length - 1,
                })}
              >
                <div className="flex items-center px-4 w-[230px] border-r border-v2-tertiary">
                  {row.label}
                </div>
                <div className="flex-1 flex items-center px-4 text-xs font-light">
                  {ATTRIBUTION_MODEL_DESCRIPTION[row.value] ?? "—"}
                </div>
                <div className="flex items-center px-4 w-[130px]">
                  {row.value === localAttributionModel ? (
                    <>
                      {isAccrual ? (
                        <PrimaryButton className="w-full">
                          SELECTED
                        </PrimaryButton>
                      ) : (
                        <NoticeButton className="w-full">SELECTED</NoticeButton>
                      )}
                    </>
                  ) : (
                    <SecondaryButton
                      className={clsx("w-full", {
                        "text-v2-action-positive hover:bg-v2-highlight-notice hover:border-v2-action-positive hover:text-v2-action-positive":
                          !isAccrual,
                      })}
                      onClick={() => {
                        setLocalAttributionModel(row.value);
                      }}
                    >
                      SELECT
                    </SecondaryButton>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex justify-end gap-2">
        {isAccrual ? (
          <>
            <TertiaryButton onClick={onClose}>CANCEL</TertiaryButton>
            <PrimaryButton
              onClick={() => {
                setGlobalAttributionModel(localAttributionModel);
                setGlobalAttributionWindow(localAttributionWindow);
                setGlobalAccountingMode(localAccountingMode);
                onClose();
              }}
            >
              APPLY
            </PrimaryButton>
          </>
        ) : (
          <>
            <TertiaryButton
              className="text-v2-action-positive hover:text-v2-alien/600 hover:bg-v2-action-positive"
              onClick={onClose}
            >
              CANCEL
            </TertiaryButton>
            <NoticeButton
              onClick={() => {
                setGlobalAttributionModel(localAttributionModel);
                setGlobalAttributionWindow(localAttributionWindow);
                setGlobalAccountingMode(localAccountingMode);
                onClose();
              }}
            >
              APPLY
            </NoticeButton>
          </>
        )}
      </div>
    </ModalPanel>
  );
};
